
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./TopSummaryCharts.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function repoPrefixFromImageId(id: string): string {
  const s = String(id ?? "");
  const m = s.match(/^([^/_-]+)/);
  return m ? m[1] : "";
}

function versionOnlyFromImageId(id: string, repo?: string): string {
  const r = repo ?? repoPrefixFromImageId(id);
  if (!r) return (id ?? "") as string;
  const rx = new RegExp(`^${escapeRegExp(r)}[/_\\-]?`, "i");
  return String(id ?? "").replace(rx, "");
}

type VulnerableVersion = { version: string; total_vulns?: number };
type ChainVersion = { version: string; total_chains?: number };
type OldNewVersion = {
  type: "oldest" | "newest";
  version: string;
  creation_time: string;
};

function TitleWithHelp({
  title,
  tip,
  id,
}: {
  title: string;
  tip: string;
  id: string;
}) {
  return (
    <div className="tsc-rowtitle">
      <h4 className="tsc-h4">{title}</h4>
      <button
        type="button"
        className="tsc-help"
        aria-label={`What is "${title}"?`}
        aria-describedby={id}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        ?
        <span id={id} className="tsc-help-tip" role="tooltip">
          {tip}
        </span>
      </button>
    </div>
  );
}

function TopSummaryCharts({ selectedImage }: { selectedImage: string }) {
  const [topVulnerable, setTopVulnerable] = useState<VulnerableVersion[]>([]);
  const [topChains, setTopChains] = useState<ChainVersion[]>([]);
  const [oldNew, setOldNew] = useState<OldNewVersion[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const repoPrefix = useMemo(
    () => repoPrefixFromImageId(selectedImage),
    [selectedImage]
  );
  const versionOnly = (raw: string) => versionOnlyFromImageId(raw, repoPrefix);

  useEffect(() => {
    if (!repoPrefix) {
      setTopVulnerable([]);
      setTopChains([]);
      setOldNew([]);
      setErr(null);
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        const bundle = await api.stats.level1Bundle(repoPrefix);
        if (ac.signal.aborted) return;

        const vulnsData = Array.isArray(bundle?.top_vulnerable)
          ? bundle.top_vulnerable
          : [];
        const chainsData = Array.isArray(bundle?.top_chains)
          ? bundle.top_chains
          : [];
        const oldNewData = Array.isArray(bundle?.oldest_newest)
          ? bundle.oldest_newest
          : [];

        setTopVulnerable(
          vulnsData.map((x: any) => ({
            version: versionOnly(x.version ?? x.Image ?? ""),
            total_vulns: x.total_vulns,
          }))
        );

        setTopChains(
          chainsData.map((x: any) => ({
            version: versionOnly(x.version ?? x.Image ?? ""),
            total_chains: x.total_chains,
          }))
        );

        setOldNew(
          oldNewData.map((x: any) => ({
            type: x.type,
            version: versionOnly(x.version ?? x.Image ?? ""),
            creation_time: x.creation_time,
          }))
        );

        setErr(null);
      } catch (e: any) {
        if (!ac.signal.aborted) {
          console.error("TopSummaryCharts level1Bundle error:", e);
          setTopVulnerable([]);
          setTopChains([]);
          setOldNew([]);
          setErr(e?.message ?? "Failed to load charts");
        }
      }
    })();

    return () => ac.abort();
  }, [repoPrefix]);

  const renderList = (
    kind: "vulns" | "chains" | "oldnew",
    data: any[],
    metricLabel: string
  ) => {
    if (!data.length) {
      return <p className="sc-empty">No data available</p>;
    }

    return (
      <ol className="sc-list">
        {data.map((item: any, index: number) => {
          const v = versionOnly(item.version);
          let metricValue: string | null = null;
          let typeTag: string | null = null;

          if (kind === "vulns") {
            metricValue =
              item.total_vulns !== undefined ? String(item.total_vulns) : null;
          } else if (kind === "chains") {
            metricValue =
              item.total_chains !== undefined ? String(item.total_chains) : null;
          } else if (kind === "oldnew") {
            typeTag =
              item.type === "oldest"
                ? "Oldest"
                : item.type === "newest"
                ? "Newest"
                : null;
            metricValue = item.creation_time
              ? new Date(item.creation_time).toLocaleDateString()
              : null;
          }

          const labelMetric =
            metricValue && metricLabel ? `${metricLabel}: ${metricValue}` : "";

          return (
            <li key={`${kind}-${v}-${index}`} className="sc-list-item">
              <Link
                to={`/vulnerabilities/${encodeURIComponent(v)}`}
                className="sc-card"
                aria-label={`Version ${v} – ${labelMetric}`}
                title={v}
              >
                <div className="sc-rank-badge">#{index + 1}</div>

                <div className="sc-card-main">
                  <div className="sc-name-row">
                    <span className="sc-name">{v}</span>
                    {typeTag && (
                      <span className="sc-type-pill">{typeTag}</span>
                    )}
                  </div>

                  {(metricValue || kind === "oldnew") && (
                    <div className="sc-meta-row">
                      {metricValue && (
                        <span className="sc-metric-tag">
                          {metricLabel}:{" "}
                          <span className="sc-metric-value">
                            {metricValue}
                          </span>
                        </span>
                      )}
                      <span className="sc-caption">
                        Click for vulnerability details
                      </span>
                    </div>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <aside className="summaryCharts-shell">
      <header className="summaryCharts-header">
        <h2 className="summaryCharts-heading">Top Charts</h2>
        <p className="summaryCharts-sub">
          Key versions for{" "}
          <code className="summaryCharts-code">
            {repoPrefix || selectedImage || "selected repo"}
          </code>
          .
        </p>
      </header>

      {err && (
        <p className="sc-error">
          Error loading stats: <span>{err}</span>
        </p>
      )}

      <section className="summaryCharts-block">
        <TitleWithHelp
          title="Top 3 Most Vulnerable"
          id="tip-most-vuln"
          tip="Versions with the highest number of known vulnerabilities (based on our scan results). Click a version to see details."
        />
        {renderList("vulns", topVulnerable, "Vulns")}
      </section>

      <section className="summaryCharts-block">
        <TitleWithHelp
          title="Oldest & Newest Versions"
          id="tip-old-new"
          tip="The oldest and newest versions detected for the selected repository (based on image creation time)."
        />
        {renderList("oldnew", oldNew, "Created")}
      </section>

      <section className="summaryCharts-block">
        <TitleWithHelp
          title="Top 3 by CVE Chains"
          id="tip-cve-chains"
          tip="Versions involved in the most CVE dependency chains (where one vulnerable package pulls in another, etc.)."
        />
        {renderList("chains", topChains, "Chains")}
      </section>
    </aside>
  );
}

export default TopSummaryCharts;
